import { useState, useEffect, useCallback } from 'react';
import type { Monitor, MonitorFilters, MonitorSortKey } from '../types/Monitor';
import { getMonitors } from '../services/MonitorService';

export interface UseMonitorsOptions {
  filters?: MonitorFilters;
  sort?: MonitorSortKey;
}

export interface UseMonitorsReturn {
  monitors: Monitor[];
  total: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetches the monitor catalogue via MonitorService.
 * Re-fetches automatically whenever filters or sort change.
 */
export function useMonitors(options: UseMonitorsOptions = {}): UseMonitorsReturn {
  const { filters, sort } = options;

  const [monitors, setMonitors] = useState<Monitor[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);

    getMonitors({ filters, sort })
      .then((result) => {
        if (cancelled) return;
        setMonitors(result.data);
        setTotal(result.total);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load monitors.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sort, tick]);

  return { monitors, total, loading, error, refetch };
}
